import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { createSearchParams } from 'react-router';

import { authSlice } from '../auth/auth.slice';
import {
  createdAtDateRangeStorageKey,
  dateRangeStorageKey,
  DownloadCsvDetails,
  driverStorageKey,
  InvoicedTripsValue,
  payersStorageKey,
  priceStorageKey,
  statusesStorageKey,
  tagsStorageKey,
} from './constants';
import { generateUploadTaskDetails } from './management/download/generateUploadDetails';
import { TRIP_FILTERS } from './tripFilters';
import type { TripsUploadSummary } from './types';
import type {
  CanceledReasonEnum,
  FilterGroup,
  InvoiceTripFinderRead,
  MembershipRead,
  OverrideStatusEnum,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { bambiApi } from 'src/common/external/bambi-api/bambiApi';
import type {
  BulkTripPriceChangeItemRead,
  BulkTripPriceChangeResultsRead,
  TripListRead,
} from 'src/common/external/bambi-api/tripsApi';
import type { NumberRangeValue } from 'src/common/primitives/NumberRangeFilterPopover/NumberRangeFilterPopover';
import { clearFiltersExtraReducer } from 'src/common/redux/clearFiltersExtraReducer';
import { searchParamsToJSON } from 'src/common/util/searchParamsToJSON';
import { setStorageValue } from 'src/common/util/useLocalStorage/setStorageValue';
export interface ITripState {
  trips: TripRead[];
  tripSearchTerm: string;
  selectedDateRange:
    | { from: string | undefined; to: string | undefined }
    | undefined;
  selectedCreatedAtDateRange:
    | { from: string | undefined; to: string | undefined }
    | undefined;
  selectedPayers: string[];
  selectedTags: string[];
  selectedDrivers: string[];
  selectedStatuses: OverrideStatusEnum[];
  downloadCsvDetail: DownloadCsvDetails;
  selectedTrip: TripRead | undefined;
  showDownloadModal: boolean;
  showDownloadDetailsModal: boolean;
  showUploadModal: boolean;
  showUploadDetailsModal: boolean;
  uploadPayerTemplate?: SelectOption;
  uploadPayerId?: SelectOption;
  uploadInProgress: boolean;
  uploadJobId?: string | null;
  csvUploadError?: TripsUploadSummary | string;
  csvUploadSuccessMessage?: string | null;
  selectedTripRows: Record<TripRead['id'], TripRead | null>;
  selectedBulkPriceTripRows: Record<
    BulkTripPriceChangeItemRead['id'],
    BulkTripPriceChangeItemRead | null
  >;
  bulkCancelModal: {
    isOpen: boolean;
    selectedTripReasons: Record<TripRead['id'], string | null>;
    tripsToCancel: TripRead[];
  };
  bulkDeleteModal: {
    isOpen: boolean;
    tripsToDelete: TripRead[];
  };
  bulkPriceModal: {
    isOpen: boolean;
    tripsToReprice: TripRead[];
    results: BulkTripPriceChangeResultsRead;
  };
  filterInvoicedTrips?: InvoicedTripsValue;
  selectedFilterGroup?: FilterGroup | null;
  tripTagModal: {
    isOpen: boolean;
    tripId: string;
  };
  tripStatusUpdatesModal: {
    isOpen: boolean;
    tripId: string;
  };
  // Vehicle IDs
  filterVehicles: string[];
  filterPrice: NumberRangeValue | null;
  filterIncludeNote: boolean;
  filterSpaceTypes: string[];
  filterCanceledReasons: CanceledReasonEnum[];
  tripFinder: {
    selectedTrips: Record<string, InvoiceTripFinderRead | undefined>;
  };
  cachedMemberships: Record<string, MembershipRead>;
  tripConfirmDeleteModal: {
    isOpen: boolean;
    trip?: TripRead | TripListRead;
  };
  selectedAllDetailsReport: string;
}

export const initialState: ITripState = {
  trips: [],
  tripSearchTerm: '',
  selectedDateRange: undefined,
  selectedCreatedAtDateRange: undefined,
  selectedPayers: [],
  selectedStatuses: [],
  selectedTags: [],
  selectedDrivers: [],
  downloadCsvDetail: DownloadCsvDetails.ALL,
  selectedTrip: undefined,
  showDownloadModal: false,
  showDownloadDetailsModal: false,
  showUploadModal: false,
  showUploadDetailsModal: false,
  uploadInProgress: false,
  csvUploadError: undefined,
  selectedTripRows: {},
  selectedBulkPriceTripRows: {},
  bulkCancelModal: {
    isOpen: false,
    selectedTripReasons: {},
    tripsToCancel: [],
  },
  bulkDeleteModal: {
    isOpen: false,
    tripsToDelete: [],
  },
  bulkPriceModal: {
    isOpen: false,
    tripsToReprice: [],
    results: {
      trips_outside_date_range: [],
      trips_no_pricing_scheme: [],
      trips_repriced: [],
    },
  },
  filterInvoicedTrips: InvoicedTripsValue.ALL_TRIPS,
  selectedFilterGroup: undefined,
  tripTagModal: {
    isOpen: false,
    tripId: '',
  },
  tripStatusUpdatesModal: {
    isOpen: false,
    tripId: '',
  },
  filterVehicles: [],
  filterPrice: null,
  filterIncludeNote: false,
  filterSpaceTypes: [],
  filterCanceledReasons: [],
  tripFinder: {
    selectedTrips: {},
  },
  cachedMemberships: {},
  tripConfirmDeleteModal: {
    isOpen: false,
  },
  selectedAllDetailsReport: 'all-details-report',
};

export const tripSlice = createSlice({
  name: 'trip',
  initialState,
  selectors: {
    getCachedMembership: (state, id: string) => {
      return state.cachedMemberships[id];
    },
  },
  reducers: {
    setTrips: (state, action: { payload: TripRead[]; type: string }) => {
      state.trips = action.payload;
    },
    setTripSearchTerm: (state, action: { payload: string; type: string }) => {
      state.tripSearchTerm = action.payload;
    },
    setSelectedDateRange: (
      state,
      action: {
        payload:
          | { from: string | undefined; to: string | undefined }
          | undefined;
        type: string;
      }
    ) => {
      const from = action.payload?.from
        ? DateTime.fromISO(action.payload.from).startOf('day').toISO()
        : undefined;
      let to = action.payload?.to
        ? DateTime.fromISO(action.payload.to).endOf('day').toISO()
        : undefined;

      if (!to && !!from) {
        to = DateTime.fromISO(from).endOf('day').toISO();
      }

      setStorageValue(dateRangeStorageKey, { from, to });
      state.selectedDateRange = { from, to };
    },
    setSelectedCreatedAtDateRange: (
      state,
      action: {
        payload:
          | { from: string | undefined; to: string | undefined }
          | undefined;
        type: string;
      }
    ) => {
      const from = action.payload?.from
        ? DateTime.fromISO(action.payload.from).startOf('day').toISO()
        : undefined;
      let to = action.payload?.to
        ? DateTime.fromISO(action.payload.to).endOf('day').toISO()
        : undefined;

      if (!to && !!from) {
        to = DateTime.fromISO(from).endOf('day').toISO();
      }

      setStorageValue(createdAtDateRangeStorageKey, { from, to });
      state.selectedCreatedAtDateRange = { from, to };
    },
    setSelectedPayers: (state, action: { payload: string[]; type: string }) => {
      setStorageValue(payersStorageKey, action.payload);
      state.selectedPayers = action.payload;
    },
    setSelectedStatuses: (
      state,
      action: { payload: OverrideStatusEnum[]; type: string }
    ) => {
      setStorageValue(statusesStorageKey, action.payload);
      state.selectedStatuses = action.payload;
    },
    setSelectedTags: (state, action: { payload: string[]; type: string }) => {
      setStorageValue(tagsStorageKey, action.payload);
      state.selectedTags = action.payload;
    },
    setSelectedDrivers: (
      state,
      action: { payload: string[]; type: string }
    ) => {
      setStorageValue(driverStorageKey, action.payload);
      state.selectedDrivers = action.payload;
    },
    setSelectedTrip: (state, action: { payload: TripRead; type: string }) => {
      state.selectedTrip = action.payload;
    },
    setShowDownloadModal: (
      state,
      action: { payload: boolean; type: string }
    ) => {
      state.showDownloadModal = action.payload;
    },
    setShowDownloadDetailsModal: (
      state,
      action: { payload: boolean; type: string }
    ) => {
      state.showDownloadDetailsModal = action.payload;
      if (!action.payload) {
        state.selectedAllDetailsReport = initialState.selectedAllDetailsReport;
      }
    },
    setSelectedAllDetailsReport: (
      state,
      action: { payload: string; type: string }
    ) => {
      state.selectedAllDetailsReport = action.payload;
    },
    setDownloadCsvDetail: (
      state,
      action: { payload: DownloadCsvDetails; type: string }
    ) => {
      state.downloadCsvDetail = action.payload;
    },
    setShowUploadModal: (state, action: { payload: boolean; type: string }) => {
      state.showUploadModal = action.payload;
    },
    setShowUploadDetailsModal: (
      state,
      action: { payload: boolean; type: string }
    ) => {
      state.showUploadDetailsModal = action.payload;
    },
    onAddTripTags: (state, action: { payload: string; type: string }) => {
      state.tripTagModal = {
        isOpen: true,
        tripId: action.payload,
      };
    },
    onCloseTripTags: (state) => {
      state.tripTagModal = {
        isOpen: false,
        tripId: '',
      };
    },
    onEditTripStatusUpdates: (
      state,
      action: { payload: string; type: string }
    ) => {
      state.tripStatusUpdatesModal = {
        isOpen: true,
        tripId: action.payload,
      };
    },
    onCloseTripStatusUpdates: (state) => {
      state.tripStatusUpdatesModal = {
        isOpen: false,
        tripId: '',
      };
    },
    onDeleteTripAction: (
      state,
      action: PayloadAction<TripRead | TripListRead>
    ) => {
      state.tripConfirmDeleteModal = {
        isOpen: true,
        trip: action.payload,
      };
    },
    onSetTripConfirmDeleteModal: (state, action: PayloadAction<boolean>) => {
      state.tripConfirmDeleteModal = {
        isOpen: action.payload,
      };
    },
    selectUploadPayerTemplate: (
      state,
      action: { payload: SelectOption; type: string }
    ) => {
      state.uploadPayerTemplate = action.payload;
    },
    selectUploadPayerId: (
      state,
      action: { payload: SelectOption; type: string }
    ) => {
      state.uploadPayerId = action.payload;
    },
    setUploadInProgress: (
      state,
      action: { payload: boolean; type: string }
    ) => {
      state.uploadInProgress = action.payload;
    },
    setCsvUploadError: (
      state,
      action: {
        payload: TripsUploadSummary | string | undefined;
        type: string;
      }
    ) => {
      state.csvUploadError = action.payload;
    },
    setUploadJobId: (
      state,
      action: { payload: string | null; type: string }
    ) => {
      state.uploadJobId = action.payload;
    },
    setCsvUploadSuccessMessage: (
      state,
      action: { payload: string | null; type: string }
    ) => {
      state.csvUploadSuccessMessage = action.payload;
    },
    setSelectedTripRows: (
      state,
      action: { payload: Record<TripRead['id'], TripRead | null> }
    ) => {
      state.selectedTripRows = action.payload;
    },
    setSelectedBulkPriceTripRows: (
      state,
      action: {
        payload: Record<
          BulkTripPriceChangeItemRead['id'],
          BulkTripPriceChangeItemRead | null
        >;
      }
    ) => {
      state.selectedBulkPriceTripRows = action.payload;
    },
    clearSelectedTripRows: (state) => {
      state.selectedTripRows = {};
    },
    removeSelectedTripRow: (state, action: { payload: TripRead['id'] }) => {
      delete state.selectedTripRows[action.payload];
    },
    onBulkCancelTripsStart: (state, action: PayloadAction<TripRead[]>) => {
      state.bulkCancelModal.tripsToCancel = action.payload;
      state.bulkCancelModal.isOpen = true;
    },
    onBulkCancelTripsDone: (state) => {
      state.bulkCancelModal = initialState.bulkCancelModal;
    },
    removeBulkCancelTrips: (state, action: PayloadAction<TripRead['id'][]>) => {
      const toRemove = new Set(action.payload);
      state.bulkCancelModal.tripsToCancel =
        state.bulkCancelModal.tripsToCancel.filter((t) => !toRemove.has(t.id));
      toRemove.forEach((id) => {
        delete state.bulkCancelModal.selectedTripReasons[id];
      });
    },
    onBulkPriceTripsStart: (state, action: PayloadAction<TripRead[]>) => {
      state.bulkPriceModal.tripsToReprice = action.payload;
      state.bulkPriceModal.isOpen = true;
    },
    onBulkPriceTripsDone: (
      state,
      action: PayloadAction<BulkTripPriceChangeResultsRead | null>
    ) => {
      if (action.payload) {
        state.bulkPriceModal.results = action.payload;
        // Initialize the bulk reprice modal with all trips selected.
        state.selectedBulkPriceTripRows = action.payload.trips_repriced.reduce(
          (acc, trip) => {
            acc[trip.id] = trip;
            return acc;
          },
          {} as Record<
            BulkTripPriceChangeItemRead['id'],
            BulkTripPriceChangeItemRead
          >
        );
      }
      state.selectedTripRows = {};
    },
    onBulkPriceTripsModalClose: (state) => {
      state.bulkPriceModal = initialState.bulkPriceModal;
      state.selectedBulkPriceTripRows = {};
      state.selectedTripRows = {};
    },
    setShowBulkCancelTripModal: (state, action: { payload: boolean }) => {
      state.bulkCancelModal.isOpen = action.payload;
    },
    setBulkCancelReasons: (
      state,
      action: PayloadAction<Record<TripRead['id'], string | null>>
    ) => {
      state.bulkCancelModal.selectedTripReasons = action.payload;
    },
    onBulkDeleteTripsStart: (state, action: PayloadAction<TripRead[]>) => {
      state.bulkDeleteModal.tripsToDelete = action.payload;
      state.bulkDeleteModal.isOpen = true;
    },
    onBulkDeleteTripsDone: (state) => {
      state.bulkDeleteModal = initialState.bulkDeleteModal;
    },
    removeBulkDeleteTrips: (state, action: PayloadAction<TripRead['id'][]>) => {
      const toRemove = new Set(action.payload);
      state.bulkDeleteModal.tripsToDelete =
        state.bulkDeleteModal.tripsToDelete.filter((t) => !toRemove.has(t.id));
    },
    setShowBulkDeleteTripModal: (state, action: { payload: boolean }) => {
      state.bulkDeleteModal.isOpen = action.payload;
    },
    setFilterInvoicedTrips: (
      state,
      action: PayloadAction<InvoicedTripsValue>
    ) => {
      state.filterInvoicedTrips = action.payload;
    },
    setFilterPrice(state, action: PayloadAction<NumberRangeValue>) {
      state.filterPrice = action.payload;
      setStorageValue(priceStorageKey, action.payload);
    },
    setFilterSpaceTypes(state, action: PayloadAction<string[]>) {
      state.filterSpaceTypes = action.payload;
    },
    setFilterCanceledReasons(
      state,
      action: PayloadAction<CanceledReasonEnum[]>
    ) {
      state.filterCanceledReasons = action.payload;
    },
    // Translates ?search into state
    initializeFilters(state) {
      const currentParams = searchParamsToJSON(
        new URLSearchParams(window.location.search)
      );

      Object.keys(currentParams).forEach((param) => {
        const rawValue = currentParams[param];

        switch (param) {
          case 'DateRange': {
            const range = JSON.parse(rawValue as string);

            state.selectedDateRange = {
              from: range.rangeStart,
              to: range.rangeEnd,
            };

            break;
          }
          case 'Payers':
          case 'Statuses':
          case 'Tags':
            // @ts-ignore
            state[`selected${param}`] = [rawValue].flatMap((n) => n);
            break;
          case 'InvoicedTrips':
            state.filterInvoicedTrips = rawValue as InvoicedTripsValue;
            break;
          case 'Price': {
            const transformedPrice: NumberRangeValue = JSON.parse(
              rawValue as string
            );
            state.filterPrice = transformedPrice;
            break;
          }
        }
      });
    },
    setSelectedFilterGroup(state, action: PayloadAction<FilterGroup | null>) {
      const payload = action.payload;
      state.selectedFilterGroup = payload;

      const currentParams = searchParamsToJSON(
        new URLSearchParams(window.location.search)
      );

      const nextParams = createSearchParams({
        // Ordering could absolutely be undefined
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ordering: currentParams.ordering ?? '',
        filterGroup: payload?.slug ?? '',
      });

      window.history.pushState(
        null,
        '',
        `${window.location.pathname}?${nextParams.toString()}`
      );

      // Translate filter group into filters
      if (!payload) {
        return;
      }

      Object.keys(payload.filters).forEach((filterName) => {
        // @ts-ignore
        const filterValue = payload.filters[filterName] ?? '';

        switch (filterName) {
          case 'range_start':
            state.selectedDateRange = {
              ...(state.selectedDateRange ?? {
                to: undefined,
              }),
              from: filterValue,
            };
            break;
          case 'range_end':
            state.selectedDateRange = {
              ...(state.selectedDateRange ?? {
                from: undefined,
              }),
              to: filterValue,
            };
            break;
          case 'has_invoice':
            state.filterInvoicedTrips = filterValue as InvoicedTripsValue;
            break;
          case 'payer__in':
            state.selectedPayers = filterValue as string[];
            break;
          case 'status__in':
            state.selectedStatuses = filterValue as OverrideStatusEnum[];
            break;
          case 'tag__in':
            state.selectedTags = filterValue as string[];
            break;
        }
      });
    },
    setFilterVehicles(state, action: PayloadAction<string[]>) {
      state.filterVehicles = action.payload;
    },
    setFilterIncludeNote(state, action: PayloadAction<boolean>) {
      state.filterIncludeNote = action.payload;
    },
    setCachedMemberships: (
      state,
      action: PayloadAction<Record<string, MembershipRead>>
    ) => {
      state.cachedMemberships = action.payload;
    },
    clearFilters(state) {
      TRIP_FILTERS.forEach((f) => f.reset(state));

      // Reset local storage keys
      setStorageValue(payersStorageKey, []);
      setStorageValue(tagsStorageKey, []);
      setStorageValue(statusesStorageKey, []);
      setStorageValue(dateRangeStorageKey, []);
      setStorageValue(createdAtDateRangeStorageKey, []);
      setStorageValue(driverStorageKey, []);
      setStorageValue(priceStorageKey, null);
    },
    setTripFinderSelectedTrip: (
      state,
      action: PayloadAction<{ tripId: string; trip: InvoiceTripFinderRead }>
    ) => {
      if (state.tripFinder.selectedTrips[action.payload.tripId]) {
        delete state.tripFinder.selectedTrips[action.payload.tripId];
      } else {
        state.tripFinder.selectedTrips[action.payload.tripId] =
          action.payload.trip;
      }
    },
    clearTripFinderSelectedTrips: (state) => {
      state.tripFinder.selectedTrips = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authSlice.actions.changeOrganization, (state) => {
      // Reset local storage keys when org changes
      setStorageValue(payersStorageKey, []);
      setStorageValue(tagsStorageKey, []);
      setStorageValue(statusesStorageKey, []);
      setStorageValue(driverStorageKey, []);
      setStorageValue(priceStorageKey, null);

      // Reset filters when org changes
      state.selectedPayers = [];
      state.selectedTags = [];
      state.selectedStatuses = [];
      state.selectedDrivers = [];
      state.filterPrice = null;
    });

    builder.addMatcher(
      bambiApi.endpoints.tripImportRetrieve.matchFulfilled,
      (state, action) => {
        switch (action.payload.state) {
          case 'STARTED':
          case 'PENDING':
            return;
        }
        state.uploadInProgress = false;
        state.uploadJobId = null;
        const details = generateUploadTaskDetails(action.payload.result);
        if (details.errors.length === 0) {
          state.csvUploadSuccessMessage = details.details;
        } else {
          // TODO: Let's just make csvUploadError a details object always
          state.csvUploadError = details;
        }
      }
    );

    clearFiltersExtraReducer(builder);

    // When any trip filter changes, clear the row selection — the rows the
    // user had selected may no longer be in view.
    builder.addMatcher(
      (action) => {
        const { actions } = tripSlice;
        return (
          actions.setSelectedDateRange.match(action) ||
          actions.setSelectedCreatedAtDateRange.match(action) ||
          actions.setSelectedPayers.match(action) ||
          actions.setSelectedStatuses.match(action) ||
          actions.setSelectedTags.match(action) ||
          actions.setSelectedDrivers.match(action) ||
          actions.setFilterInvoicedTrips.match(action) ||
          actions.setFilterVehicles.match(action) ||
          actions.setFilterPrice.match(action) ||
          actions.setFilterSpaceTypes.match(action) ||
          actions.setFilterCanceledReasons.match(action) ||
          actions.setFilterIncludeNote.match(action) ||
          actions.setTripSearchTerm.match(action) ||
          actions.setSelectedFilterGroup.match(action) ||
          actions.clearFilters.match(action)
        );
      },
      (state) => {
        if (Object.keys(state.selectedTripRows).length > 0) {
          state.selectedTripRows = {};
        }
      }
    );

    // Translates filter payload into ?search
    builder.addMatcher(
      (action) => {
        const type: string = action.type;
        return [
          'trip/setSelectedDateRange',
          'trip/setSelectedPayers',
          'trip/setSelectedStatuses',
          'trip/setSelectedTags',
          'trip/setFilterInvoicedTrips',
          'trip/setFilterPrice',
        ].includes(type);
      },
      (state, action: PayloadAction) => {
        const type: string = action.type;
        const payload: unknown = action.payload;
        const filterParam = type.match(/set(Filter|Selected)([a-z]+)/i)?.[2];

        if (!filterParam) {
          return state;
        }

        const value: string | string[] | undefined = (() => {
          switch (filterParam) {
            case 'Price':
            case 'DateRange':
              return JSON.stringify(payload);
            case 'Payers':
            case 'Statuses':
            case 'Tags':
              return payload as string[];
            case 'InvoicedTrips':
              return payload as string;
          }
        })();

        if (value) {
          const currentParams = searchParamsToJSON(
            new URLSearchParams(window.location.search)
          );
          const nextParams = createSearchParams({
            ...currentParams,
            [filterParam]: value,
          });

          window.history.pushState(
            null,
            '',
            `${window.location.pathname}?${nextParams.toString()}`
          );
        }

        return state;
      }
    );
  },
});

export const {
  setTrips,
  setTripSearchTerm,
  setSelectedDateRange,
  setSelectedCreatedAtDateRange,
  setSelectedPayers,
  setSelectedStatuses,
  setSelectedTags,
  setSelectedDrivers,
  setSelectedTrip,
  setDownloadCsvDetail,
  setShowDownloadModal,
  setShowDownloadDetailsModal,
  setShowUploadModal,
  selectUploadPayerTemplate,
  selectUploadPayerId,
  setShowUploadDetailsModal,
  setUploadInProgress,
  setCsvUploadError,
  setUploadJobId,
  setCsvUploadSuccessMessage,
  setSelectedTripRows,
  clearSelectedTripRows,
  removeSelectedTripRow,
  removeBulkCancelTrips,
  setShowBulkCancelTripModal,
  removeBulkDeleteTrips,
  setShowBulkDeleteTripModal,
  setFilterInvoicedTrips,
  initializeFilters,
  setSelectedFilterGroup,
  clearFilters,
  setFilterVehicles,
  setFilterPrice,
  setFilterSpaceTypes,
  setTripFinderSelectedTrip,
  clearTripFinderSelectedTrips,
  setFilterIncludeNote,
  setCachedMemberships,
  setFilterCanceledReasons,
  setSelectedAllDetailsReport,
} = tripSlice.actions;

export const { getCachedMembership } = tripSlice.selectors;

export function selectHasActiveTripFilters(state: {
  trip: ITripState;
}): boolean {
  return TRIP_FILTERS.some((f) => f.isActive(state.trip));
}
